<script setup lang="ts">
import { computed, Component, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import type {
  EvaluationType,
  EvaluationListType,
} from "@/common/types/evaluation";

import { PanelTheme } from "../";

import {
  baseInput,
  baseConfirm,
  baseSelect,
  svgIcon,
  tabMenu,
  iconMenu,
  cardEvaluation,
} from "@/common/components";

interface TabListType {
  [key: string]: {
    key: string;
    icon: string;
    label: string;
    component: Component;
  };
}

const { t } = useI18n();
const router = useRouter();

const emit = defineEmits([
  "update-data",
  "add",
  "input-search",
  "search",
  "select-tab",
  "change-status",
]);

const props = defineProps<{
  accountId: string;
  evaluationId: string | null;
  evaluation: EvaluationType | null;
  evaluations: EvaluationListType[];
  tabList: TabListType;
  menuList: any;
  isEdit: boolean;
}>();

const searchInput = ref("");

const selectedTab = ref<string | null>(null);
const statusType = ref("PROGRESS");

const statusOptions = computed(() => [
  {
    option: t("evaluation.labels.in_progress"),
    value: "PROGRESS",
  },
  {
    option: t("evaluation.labels.started"),
    value: "STARTED",
  },
  {
    option: t("evaluation.labels.pending"),
    value: "PENDING",
  },
  {
    option: t("evaluation.labels.concluded"),
    value: "CONCLUDED",
  },
  {
    option: t("evaluation.labels.canceled"),
    value: "CANCELED",
  },
]);

function selectTab(tab: string) {
  selectedTab.value = tab;
  emit("update-data");
}

function selectEvaluation(evaluation_id: string) {
  router.push(`/${props.accountId}/cockpit/${evaluation_id}`);
  setTimeout(() => selectTab("info"), 100);
}

function back() {
  router.push(`/${props.accountId}/cockpit`);
}

onMounted(() => {
  const status = localStorage.getItem("cockpit-status");
  statusType.value = status || "PROGRESS";

  selectTab("info");
});
</script>

<template>
  <panel-theme>
    <div class="header">
      <base-select
        v-model="statusType"
        :value="statusType"
        :options="statusOptions"
        :is-empty="false"
        :disabled="true"
      />

      <div class="search">
        <base-input
          v-model="searchInput"
          background="#fff"
          color="#000"
          :placeholder="t('common.labels.search')"
          @update:model-value="emit('input-search', searchInput)"
          @key:enter="emit('search', searchInput)"
        >
          <template #sufix>
            <div class="icon">
              <svg-icon
                icon="search"
                size="1.75rem"
                @click="emit('search', searchInput)"
              />
            </div>
          </template>
        </base-input>
      </div>

      <div class="actions">
        <button class="btn btn-md btn-primary" @click="emit('add')">
          <svg-icon icon="add" size="1.1rem" />
          {{ t("common.labels.evaluation")?.toUpperCase() }}
        </button>

        <button type="button" class="btn btn-md btn-back" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </div>

    <div class="content">
      <div class="list">
        <template v-for="item in evaluations" :key="item.evaluation_id">
          <card-evaluation
            avatar-size="4rem"
            :evaluation="item"
            :active="item.evaluation_id === evaluationId"
            :show-progress="false"
            :show-arrow="true"
            @click="selectEvaluation(item.evaluation_id)"
          />
        </template>
      </div>

      <div class="evaluation">
        <tab-menu
          :tabs="menuList"
          :active="selectedTab"
          @select:tab="selectTab($event)"
        >
          <template #content>
            <template v-if="selectedTab">
              <component
                :is="tabList[selectedTab]?.component"
                :is-edit="isEdit"
                :account-id="accountId"
                :evaluation-id="evaluationId"
                :data="evaluation"
                @update-data="$emit('update-data')"
              />
            </template>
          </template>
        </tab-menu>
      </div>
    </div>
  </panel-theme>
</template>

<style lang="scss" scoped>
.header {
  display: grid;
  grid-template-columns: 18rem 2fr auto;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  margin-block-end: 1rem;

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .actions {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
  }
}

.content {
  display: flex;
  flex-direction: row;
  gap: 1rem;

  .list {
    display: flex;
    flex-direction: column;
    gap: 0.57rem;
    overflow-y: auto;
    padding-inline-end: 0.5rem;
    padding-block-end: 1rem;
    height: calc(78dvh - 1.6rem);
  }

  .evaluation {
    display: flex;
    flex: 1;
    flex-direction: column;
    height: calc(80.5dvh - 1.7rem);
    overflow-y: auto;
  }
}
</style>
